import { BOEntityDefinition, PanelSourceMetadata, ImisResponse, PayloadFieldToTypeMapping } from "../models/imis.models";
import { DocumentsUtility } from "./documents.utility";
import { PayloadUtility } from "./payload.utility";
import { RestUtility } from "./rest.utility";

/**
 * Utilities for reading and managing iMIS business object/data source definitions and their individual records.
 */
export class DataSourcesUtility {

    /**
     * Retrieves iMIS business object entity definitions—either only true panel data sources (queried directly from the `boentitydefinition` endpoint), or all Business Object Definition (BOD) documents with each one's data source definition fetched individually.
     * @param onlyPanelSources when true, fetch definitions only from the panel-source `boentitydefinition` endpoint; when false, look up every BOD document and fetch each one's data source definition individually
     * @param isSystem whether to include system business object definitions when `onlyPanelSources` is false
     * @returns array of business object entity definitions
     */
    public static async getAllDataSources(onlyPanelSources: boolean, isSystem: boolean): Promise<BOEntityDefinition[]> {
        let boEntityDefinitionItems: BOEntityDefinition[] = [];

        if (onlyPanelSources) {
            let response = await RestUtility.restApiGetDataAll<BOEntityDefinition>("/api/boentitydefinition");
            boEntityDefinitionItems = response.Items;
        } else {
            let businessObjectDefinitionsResponse = await DocumentsUtility.getDocumentsByType("BOD", isSystem, true);
            let businessObjectDefinitionsNames = businessObjectDefinitionsResponse.Items.map(i => i.Name);
            
            let promises = businessObjectDefinitionsNames.map(bodName => this.getDataSourceDefinition(bodName));
            boEntityDefinitionItems = await Promise.all(promises);
        }

        return boEntityDefinitionItems; 
    }

    /**
     * Fetches the entity definition for a single named data source.
     * @param dataSourceName name of the data source/business object to fetch
     * @param rawResponse when true, return the response unparsed as sent by iMIS
     * @returns the entity definition, or null if the request failed
     */
    public static async getDataSourceDefinition(dataSourceName: string, rawResponse?: boolean): Promise<BOEntityDefinition> {
        let boEntityDefinition: BOEntityDefinition;

        try {
            boEntityDefinition = await RestUtility.restApiGetData<BOEntityDefinition>(`/api/boentitydefinition/${dataSourceName}`, null, rawResponse);
        } catch (error) {
            boEntityDefinition = null;
        }

        return boEntityDefinition;
    }

    /**
     * Fetches panel source metadata (field/property definitions) for a named data source.
     * @param dataSourceName name of the data source
     * @returns the metadata, or null if the request failed
     */
    public static async getDataSourceMetadata(dataSourceName: string): Promise<PanelSourceMetadata> {
        let panelSourceItem: PanelSourceMetadata;

        try {
            panelSourceItem = await RestUtility.restApiGetData<PanelSourceMetadata>(`/api/metadata/${dataSourceName}`);
        } catch (error) {
            panelSourceItem = null;
        }

        return panelSourceItem;
    }

    /**
     * Fetches a single data source record by its identity value(s).
     * @param tableName data source name
     * @param identity array of identity values identifying the record (e.g. `[id, ordinal]` or `[ordinal]`)
     * @param rawResponse when true, return the response unparsed as sent by iMIS
     * @returns the requested record
     */
    public static async getDataSourceRecord<T = ImisResponse>(tableName: string, identity: any[], rawResponse?: boolean): Promise<T> {
        let url = this.getRequestUrl(tableName, identity);

        return RestUtility.restApiGetData(url, null, rawResponse);
    }

    /**
     * 
     * @param tableName data source name
     * @param payload key-value pairs of mappings between field names and values of exactly the data type expected by the datasource (i.e. integers cannot be strings)
     * @param fieldToTypeMapping (optional) if the field does not have a standard mapping to a literal data type then the field can be specified here
     * @param rawResponse dont parse the payload and return it as iMIS sends it (great for when you need to post the changes back) 
     * @returns the imis response 
     */
    public static async addDataSourceRecord(tableName: string, payload: any, fieldToTypeMapping?: PayloadFieldToTypeMapping, rawResponse?: boolean): Promise<any> {
        let url: string = "/api/" + tableName;
        let mappedJsonPayload = PayloadUtility.getPanelSourcePayload(tableName, null, payload, null, fieldToTypeMapping);

        return RestUtility.restApiSendData(url, mappedJsonPayload, "POST", rawResponse);
    }

    /**
     * 
     * @param tableName data source name
     * @param identity an array of values that identify the unique identifiers for the datasource (possible combinations: [id, ordinal] or [ordinal])
     * @param payload key-value pairs of mappings between field names and values of exactly the data type expected by the datasource (i.e. integers cannot be strings)
     * @param alternativeIdentityFieldNames (optional) if the field names are not the default `ID` as the primary and `Ordinal` as the secondary then this is where you would specify the alternative names
     * @param fieldToTypeMapping (optional) if the field does not have a standard mapping to a literal data type then the field can be specified here
     * @param rawResponse dont parse the payload and return it as iMIS sends it (great for when you need to post the changes back)
     * @returns the imis response
     */
    public static async updateDataSourceRecord(tableName: string, identity: any[], payload: any, alternativeIdentityFieldNames?: string[], fieldToTypeMapping?: PayloadFieldToTypeMapping, rawResponse?: boolean): Promise<any> {
        let url = this.getRequestUrl(tableName, identity);
        let mappedJsonPayload = PayloadUtility.getPanelSourcePayload(tableName, identity, payload, alternativeIdentityFieldNames, fieldToTypeMapping);

        return RestUtility.restApiSendData(url, mappedJsonPayload, "PUT", rawResponse);
    }

    /**
     * Deletes a single data source record identified by its identity value(s).
     * @param tableName data source name
     * @param identity array of identity values identifying the record to delete
     * @returns true if the deletion succeeded, false otherwise
     */
    public static async deleteDataSourceRecord(tableName: string, identity: any[]): Promise<boolean> {
        let url = this.getRequestUrl(tableName, identity);

        return RestUtility.restApiDeleteData(url);
    }

    /**
     * Copies an existing business object entity definition under a new name/description to create a new data source.
     * @param targetSourceName name of the existing data source to copy
     * @param newSourceName name to assign to the new duplicated data source
     * @returns the newly created entity definition (raw iMIS response)
     */
    public static async duplicateDataSourceDefinition(targetSourceName: string, newSourceName: string): Promise<BOEntityDefinition> {
        let newDataSource = await this.getDataSourceDefinition(targetSourceName, true);
        newDataSource.EntityTypeName = newSourceName;
        newDataSource.Description = `Duplicate datasource of ${targetSourceName}`;

        return RestUtility.restApiSendData("/api/boentitydefinition", newDataSource, "POST", true);
    }

    /**
     * Builds a data source record URL from a table name and its pipe-delimited identity values.
     * @param tableName data source name
     * @param identity array of identity values to encode into the URL
     * @returns the URL path for the specific record
     */
    private static getRequestUrl(tableName: string, identity: any[]): string {
        return "/api/" + tableName + "/~" + identity.join("|");
    }
}